export interface SEOData {
  title: string;
  description: string;
  keywords: string[];
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  structuredData?: any;
  alternateLanguages?: { lang: string; url: string }[];
}

export const defaultSEO: SEOData = {
  title: "Dr Youssef Gam - Chirurgien Esthétique et Plastique | Tunis & Paris",
  description:
    "Dr Youssef Gam, chirurgien esthétique et plastique de référence à Tunis et Paris. Expert en rhinoplastie, augmentation mammaire, liposuccion, lifting, botox et acide hyaluronique. Consultations à Tunis et Paris.",
  keywords: [
    "chirurgien esthétique tunisie",
    "chirurgien esthétique paris",
    "chirurgie plastique tunis",
    "chirurgie esthétique paris",
    "dr youssef gam",
    "rhinoplastie tunisie",
    "rhinoplastie paris",
    "augmentation mammaire tunisie",
    "augmentation mammaire paris",
    "liposuccion tunisie",
    "liposuccion paris",
    "lifting visage",
    "botox tunisie",
    "botox paris",
    "acide hyaluronique tunisie",
    "acide hyaluronique paris",
    "meilleur chirurgien esthétique tunisie",
  ],
  ogImage: "https://www.dryoussefgam.com/logo-black.png",
  ogType: "website",
};

export const updateMetaTags = (seoData: Partial<SEOData>) => {
  const data = { ...defaultSEO, ...seoData };

  // Update title
  document.title = data.title;

  // Update meta description
  updateMetaTag("description", data.description);

  // Update keywords
  updateMetaTag("keywords", data.keywords.join(", "));

  // Update canonical URL
  if (data.canonicalUrl) {
    updateLinkTag("canonical", data.canonicalUrl);
  }

  // Update Open Graph tags
  updateMetaProperty("og:title", data.title);
  updateMetaProperty("og:description", data.description);
  updateMetaProperty("og:type", data.ogType || "website");
  updateMetaProperty("og:url", window.location.href);

  if (data.ogImage) {
    updateMetaProperty("og:image", data.ogImage);
    updateMetaProperty("og:image:alt", data.title);
  }

  // Update Twitter Card tags
  updateMetaName("twitter:card", "summary_large_image");
  updateMetaName("twitter:title", data.title);
  updateMetaName("twitter:description", data.description);

  if (data.ogImage) {
    updateMetaName("twitter:image", data.ogImage);
  }

  // Add structured data
  if (data.structuredData) {
    addStructuredData(data.structuredData);
  }
};

const updateMetaTag = (name: string, content: string) => {
  let element = document.querySelector(`meta[name="${name}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("name", name);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
};

const updateMetaProperty = (property: string, content: string) => {
  let element = document.querySelector(`meta[property="${property}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("property", property);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
};

const updateMetaName = (name: string, content: string) => {
  let element = document.querySelector(`meta[name="${name}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("name", name);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
};

const updateLinkTag = (rel: string, href: string) => {
  let element = document.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
};

const addStructuredData = (data: any) => {
  // Remove existing structured data
  const existing = document.querySelector('script[type="application/ld+json"]');
  if (existing) {
    existing.remove();
  }

  // Add new structured data
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.innerHTML = JSON.stringify(data);
  document.head.appendChild(script);
};

// Structured data schemas
export const createDoctorSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Physician",
  name: "Dr Youssef Gam",
  jobTitle: "Chirurgien Esthétique et Plastique",
  description:
    "Chirurgien esthétique et plastique de référence exerçant à Tunis et Paris. Spécialiste en chirurgie du visage, des seins, du corps et médecine esthétique.",
  url: "https://www.dryoussefgam.com",
  image: "https://www.dryoussefgam.com/logo-black.png",
  email: "dr.youssef.gam@gmail.com",
  telephone: ["+21693733220", "+33673277948"],
  address: [
    {
      "@type": "PostalAddress",
      streetAddress: "Cabinet du Dr. Youssef Gam, Regen Cell Clinic",
      addressLocality: "Tunis",
      addressRegion: "Tunis",
      addressCountry: "TN",
    },
    {
      "@type": "PostalAddress",
      addressLocality: "Paris",
      addressRegion: "Île-de-France",
      addressCountry: "FR",
    },
  ],
  areaServed: [
    { "@type": "City", name: "Tunis" },
    { "@type": "City", name: "Paris" },
    { "@type": "Country", name: "Tunisie" },
    { "@type": "Country", name: "France" },
  ],
  medicalSpecialty: "PlasticSurgery",
  specialty: [
    "Chirurgie Esthétique",
    "Chirurgie Plastique",
    "Médecine Esthétique",
  ],
  memberOf: {
    "@type": "Organization",
    name: "Ordre National des Médecins de Tunisie",
  },
  knowsLanguage: ["fr", "ar", "en"],
});

export const createMedicalBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "MedicalBusiness",
  name: "Cabinet Dr Youssef Gam",
  description:
    "Cabinet de chirurgie esthétique et plastique du Dr Youssef Gam. Consultations à Tunis et Paris.",
  url: "https://www.dryoussefgam.com",
  email: "dr.youssef.gam@gmail.com",
  telephone: ["+21693733220", "+33673277948"],
  address: [
    {
      "@type": "PostalAddress",
      streetAddress: "Cabinet du Dr. Youssef Gam, Regen Cell Clinic",
      addressLocality: "Tunis",
      addressRegion: "Tunis",
      addressCountry: "TN",
    },
    {
      "@type": "PostalAddress",
      addressLocality: "Paris",
      addressRegion: "Île-de-France",
      postalCode: "75000",
      addressCountry: "FR",
    },
  ],
  geo: {
    "@type": "GeoCoordinates",
    latitude: "36.8065",
    longitude: "10.1815",
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "17:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: "Saturday",
      opens: "09:00",
      closes: "13:00",
    },
  ],
  priceRange: "€€€",
  medicalSpecialty: "PlasticSurgery",
  areaServed: [
    { "@type": "City", name: "Tunis" },
    { "@type": "City", name: "Paris" },
    { "@type": "Country", name: "Tunisie" },
    { "@type": "Country", name: "France" },
  ],
  image: "https://www.dryoussefgam.com/logo-black.png",
});

export const createServiceSchema = (
  serviceName: string,
  description: string,
) => ({
  "@context": "https://schema.org",
  "@type": "MedicalProcedure",
  name: serviceName,
  description: description,
  performer: {
    "@type": "Physician",
    name: "Dr Youssef Gam",
  },
  location: [
    {
      "@type": "MedicalOrganization",
      name: "Cabinet Dr Youssef Gam - Tunis",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Cabinet du Dr. Youssef Gam, Regen Cell Clinic",
        addressLocality: "Tunis",
        addressCountry: "TN",
      },
    },
    {
      "@type": "MedicalOrganization",
      name: "Cabinet Dr Youssef Gam - Paris",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Paris",
        addressCountry: "FR",
      },
    },
  ],
});

export const createBlogPostSchema = (article: {
  title: string;
  description: string;
  publishDate: string;
  author: string;
  image: string;
  url: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: article.title,
  description: article.description,
  image: article.image,
  author: {
    "@type": "Person",
    name: article.author,
    url: "https://www.dryoussefgam.com/biographie",
  },
  publisher: {
    "@type": "Organization",
    name: "Dr Youssef Gam",
    logo: {
      "@type": "ImageObject",
      url: "https://www.dryoussefgam.com/logo-black.png",
    },
  },
  datePublished: article.publishDate,
  dateModified: article.publishDate,
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": article.url,
  },
  inLanguage: "fr",
});

export const createFAQSchema = (
  faqs: { question: string; answer: string }[],
) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
});

export const createBreadcrumbSchema = (
  items: { name: string; url: string }[],
) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});
